

import { motion } from "motion/react";
import { Globe, CheckCircle2, Zap, ShieldCheck, BarChart3, Users } from "lucide-react";

export const Features = () => {
  const features = [
    { title: "AI-Powered Screening", icon: <Globe className="w-6 h-6" />, desc: "Neural processing of candidate profiles to ensure technical and soft-skill fit." },
    { title: "Intelligent Shortlisting", icon: <CheckCircle2 className="w-6 h-6" />, desc: "Automated ranking of candidates, so you only see the top 1% of applicants." },
    { title: "Continuous Pipeline", icon: <Zap className="w-6 h-6" />, desc: "Active candidate generation that doesn't stop until your role is closed." },
    { title: "Standardized Frameworks", icon: <ShieldCheck className="w-6 h-6" />, desc: "Unbiased, objective evaluation metrics across all your open positions." },
    { title: "Hiring Analytics", icon: <BarChart3 className="w-6 h-6" />, desc: "Complete visibility into your hiring funnel with data-backed insights." },
    { title: "Outcome Guarantee", icon: <Users className="w-6 h-6" />, desc: "Zero risk model. You only pay for successful placements." }
  ];

  return (
    <section id="features" className="py-40 bg-brand-primary text-white rounded-[6rem] mx-4 sm:mx-8 shadow-2xl relative overflow-hidden group">
      <div className="blob w-[600px] h-[600px] bg-brand-accent top-[-20%] right-[-10%] opacity-10" />
      <div className="blob w-[400px] h-[400px] bg-brand-deep bottom-[-10%] left-[-10%] opacity-10" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-24">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-6xl md:text-8xl font-display font-bold tracking-tight mb-8">Built for outcome-driven hiring</h2>
            <p className="text-2xl text-slate-400 max-w-3xl mx-auto font-medium italic">A platform-powered recruitment engine built to deliver outcomes — not just activity.</p>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((f, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              whileHover={{ scale: 1.05, rotate: i % 2 === 0 ? 1 : -1 }}
              className="p-10 rounded-[3rem] bg-zinc-900/50 backdrop-blur-xl border border-zinc-800 hover:border-brand-accent/50 transition-all duration-500 relative group/card overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-brand-accent/0 to-brand-accent/5 opacity-0 group-hover/card:opacity-100 transition-opacity" />
              <div className="w-16 h-16 rounded-2xl bg-zinc-800 flex items-center justify-center mb-8 group-hover/card:bg-brand-accent group-hover/card:text-black transition-colors shadow-xl">
                {f.icon}
              </div>
              <h3 className="text-2xl font-bold mb-4 tracking-tight group-hover/card:text-brand-accent transition-colors">{f.title}</h3>
              <p className="text-zinc-400 font-bold leading-relaxed text-lg">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
